import { create } from "zustand";
import axios from "axios";
import { backend_url } from "../../constants";
import { AXIOS_CONFIG, getNotifications } from "../api";
import { Endpoints } from "../api/endpoints";
import type { OrganizerStatus } from "../constants/TaxOrganizer";
import { generateTaxYear } from "@/utilities";

/**
 * Represents a tax organizer in the list
 */
export interface TaxOrganizerListItem {
  year: number;
  status: OrganizerStatus | null;
  lastUpdated?: string;
  isComplete?: boolean;
  hasEngagementLetter?: boolean;
}

/**
 * Store for managing the list of tax organizers
 */
interface TaxOrganizerListStore {
  // State
  organizers: TaxOrganizerListItem[];
  isLoading: boolean;
  error: Error | null;
  lastFetchTime: number | null;
  needsRefresh: boolean;

  // Actions
  setOrganizers: (organizers: TaxOrganizerListItem[]) => void;
  updateOrganizer: (
    year: number,
    updates: Partial<TaxOrganizerListItem>
  ) => void;
  addOrganizer: (organizer: TaxOrganizerListItem) => void;
  removeOrganizer: (year: number) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  markNeedsRefresh: () => void;
  clearNeedsRefresh: () => void;
  reset: () => void;

  // Async actions
  fetchAllYearsData: (userId: string) => Promise<void>;

  // Getters
  getOrganizerByYear: (year: number) => TaxOrganizerListItem | undefined;
  getSortedOrganizers: () => TaxOrganizerListItem[];
}

const getDefaultState = () => ({
  organizers: [],
  isLoading: false,
  error: null as Error | null,
  lastFetchTime: null,
  needsRefresh: false,
});

/**
 * Zustand store for managing tax organizer list
 *
 * This store manages the list of tax organizers displayed on the home page.
 * It provides actions to update, add, remove organizers and track refresh state.
 *
 * Usage:
 * ```typescript
 * const { organizers, setOrganizers, updateOrganizer } = useTaxOrganizerListStore();
 * ```
 */
export const useTaxOrganizerListStore = create<TaxOrganizerListStore>()(
  (set, get) => ({
    ...getDefaultState(),

    setOrganizers: (organizers: TaxOrganizerListItem[]) => {
      set({
        organizers,
        lastFetchTime: Date.now(),
        error: null,
      });
    },

    updateOrganizer: (year: number, updates: Partial<TaxOrganizerListItem>) => {
      set((state) => ({
        organizers: state.organizers.map((org) =>
          org.year === year ? { ...org, ...updates } : org
        ),
      }));
    },

    addOrganizer: (organizer: TaxOrganizerListItem) => {
      set((state) => {
        const exists = state.organizers.some(
          (org) => org.year === organizer.year
        );
        if (exists) {
          return {
            organizers: state.organizers.map((org) =>
              org.year === organizer.year ? organizer : org
            ),
          };
        }
        return {
          organizers: [...state.organizers, organizer].sort(
            (a, b) => b.year - a.year
          ),
        };
      });
    },

    removeOrganizer: (year: number) => {
      set((state) => ({
        organizers: state.organizers.filter((org) => org.year !== year),
      }));
    },

    setLoading: (isLoading: boolean) => {
      set({ isLoading });
    },

    setError: (error: Error | null) => {
      set({ error });
    },

    markNeedsRefresh: () => {
      set({ needsRefresh: true });
    },

    clearNeedsRefresh: () => {
      set({ needsRefresh: false });
    },

    reset: () => {
      set(getDefaultState());
    },

    /**
     * Fetch all years data from backend
     * This is an optimized single API call that returns all tax years and their statuses
     */
    fetchAllYearsData: async (userId: string) => {
      // Validation
      if (!userId) {
        set({ error: new Error("userId is required") });
        return;
      }

      const state = get();

      // Prevent duplicate fetches
      if (state.isLoading) {
        console.log(
          "Already loading organizer list data, skipping duplicate fetch"
        );
        return;
      }

      console.log("fetchAllYearsData - Starting fetch:", { userId });

      // Set loading state
      set({
        isLoading: true,
        error: null,
      });

      try {
        // Fetch organizer data and notifications in parallel for better performance
        const [response] = await Promise.all([
          axios.get(`${backend_url}${Endpoints.organizer}`, {
            params: {
              clientId: userId,
              allYears: true,
            },
            ...AXIOS_CONFIG,
          }),
          // Fetch notifications as part of initialization
          getNotifications({}).catch((error) => {
            console.error(
              "fetchAllYearsData - Notification fetch failed:",
              error
            );
            // Don't fail the whole initialization if notifications fail
            return null;
          }),
        ]);

        const content = response?.data?.content ?? {};
        const allTaxYears = content.allTaxYears ?? [];
        const yearStatuses = content.yearStatuses ?? {};

        console.log("fetchAllYearsData - Success:", {
          yearsCount: allTaxYears.length,
          years: allTaxYears,
          statuses: yearStatuses,
        });

        // Get years from backend only
        const yearsFromBackend = (allTaxYears || [])
          .map((year: string) => parseInt(year, 10))
          .filter((year: number) => !isNaN(year) && year >= 2020); // Show years 2020+

        console.log(
          "fetchAllYearsData - Years from backend (filtered):",
          yearsFromBackend
        );

        // If no years from backend, default to current year
        const currentTaxYear = generateTaxYear();
        const yearsToShow =
          yearsFromBackend.length > 0 ? yearsFromBackend : [currentTaxYear];

        console.log("fetchAllYearsData - Years to show:", yearsToShow);

        // Map to organizer list items with statuses
        const organizers: TaxOrganizerListItem[] = yearsToShow.map(
          (year: number) => ({
            year,
            status: (yearStatuses[year.toString()] as OrganizerStatus) ?? null,
          })
        );

        // Sort descending (newest first)
        organizers.sort((a, b) => b.year - a.year);

        console.log("fetchAllYearsData - Setting organizers:", organizers);

        // Update store
        set({
          organizers,
          isLoading: false,
          error: null,
          lastFetchTime: Date.now(),
        });
      } catch (error) {
        console.error("fetchAllYearsData - Error:", error);
        set({
          error: error as Error,
          isLoading: false,
          organizers: [],
        });
        throw error;
      }
    },

    getOrganizerByYear: (year: number) => {
      return get().organizers.find((org) => org.year === year);
    },

    getSortedOrganizers: () => {
      return [...get().organizers].sort((a, b) => b.year - a.year);
    },
  })
);
